/**
 * Format a date string or timestamp to a readable date/time
 */
export const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Calculate duration between start and end timestamps in seconds
 * @param {string|Date} startTime - Start time
 * @param {string|Date} endTime - End time
 * @param {Object} syncObj - Optional sync object with updatedAt/createdAt fields as fallback
 * @returns {number} Duration in seconds
 */
export const calculateDuration = (startTime, endTime, syncObj) => {
  if (!startTime) return null;
  
  const start = new Date(startTime);
  
  // If endTime exists and is valid, use it
  if (endTime && endTime !== 'N/A') {
    const end = new Date(endTime);
    if (!isNaN(end.getTime())) {
      return Math.round((end - start) / 1000);
    }
  }
  
  // If no endTime but sync object has updatedAt, use it as primary fallback
  if (syncObj && syncObj.updatedAt) {
    const updatedAt = new Date(syncObj.updatedAt);
    if (!isNaN(updatedAt.getTime())) {
      return Math.round((updatedAt - start) / 1000);
    }
  }
  
  // If no updatedAt but sync object has createdAt, use it as secondary fallback
  if (syncObj && syncObj.createdAt) {
    const createdAt = new Date(syncObj.createdAt);
    if (!isNaN(createdAt.getTime())) {
      return Math.round((createdAt - start) / 1000);
    }
  }
  
  // Otherwise use current time (for running syncs)
  const now = new Date();
  
  if (isNaN(start.getTime())) {
    return null;
  }
  
  // Return duration in seconds
  return Math.round((now - start) / 1000);
};

/**
 * Format a duration in seconds to a readable string
 * Can also accept start and end times to calculate duration
 * @param {number|string|Date|Object} input - Duration in seconds, startTime, or sync object
 * @param {string|Date} endTime - Optional end time if input is startTime
 * @returns {string} Formatted duration string
 */
export const formatDuration = (input, endTime) => {
  let seconds;
  
  // If input is a plain number, use it directly
  if (typeof input === 'number' || (typeof input === 'string' && !isNaN(Number(input)))) {
    seconds = Number(input);
  }
  // Check if we received an object with startTime/endTime
  else if (input && typeof input === 'object' && input.startTime) {
    // For running syncs (status === 'running'), calculate duration from startTime to now
    if (input.status === 'running') {
      const start = new Date(input.startTime);
      const now = new Date();
      seconds = Math.round((now - start) / 1000);
    } else {
      // For completed syncs, use the existing calculation
      seconds = calculateDuration(input.startTime, input.endTime, input);
    }
  } 
  // Check if we received startTime and endTime parameters
  else if (typeof endTime !== 'undefined') {
    seconds = calculateDuration(input, endTime);
  }
  // Otherwise assume input is already seconds (but handle invalid inputs)
  else {
    seconds = input;
  }
  
  if (!seconds && seconds !== 0) return 'N/A';
  
  if (seconds < 60) {
    return `${Math.round(seconds)} seconds`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${remainingMinutes}m`;
  }
};

/**
 * Format a sync type for display
 */
export const formatSyncType = (type) => {
  if (!type) return 'Unknown';
  
  // Remove the "-sync" suffix if present
  const baseType = type.replace(/-sync$/, '');
  
  // Capitalize first letter of each word
  return baseType
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Get status badge class based on status
 */
export const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'running':
      return 'bg-primary';
    case 'completed':
      return 'bg-success';
    case 'failed':
      return 'bg-danger';
    default:
      return 'bg-secondary';
  }
}; 